import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, PiggyBank, CreditCard, FileCheck2, Video, Headphones, CalendarCheck, Mail, ArrowUpRight, Landmark } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { EASE } from "@/lib/motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PERKS = [
  { id: "photo-upload", Icon: Camera, text: "Upload your documents by taking pictures!", to: "/portal" },
  { id: "pay-refund", Icon: PiggyBank, text: "Pay Us by Refund!" },
  { id: "pay-card", Icon: CreditCard, text: "Pay by credit card!", to: "/portal" },
  { id: "efile-pdf", Icon: FileCheck2, text: "E-File + PDF Taxes!" },
  { id: "zoom", Icon: Video, text: "Zoom Meetings — 10am–7pm M–F, by request Sat/Sun", to: "/book" },
  { id: "support", Icon: Headphones, text: "Human Support Center!", modal: true },
  { id: "year-round", Icon: CalendarCheck, text: "Year round tax service!" },
];

export const HeroPerks = () => {
  const [supportOpen, setSupportOpen] = useState(false);
  const itemClass = "group flex w-full items-start gap-3 text-left";

  return (
    <>
      <div className="glass border border-steel-border bg-steel-surface/70 p-5" data-testid="hero-perks">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-steel-brass">
          What you get
        </p>
        <ul className="mt-4 space-y-2.5">
          {PERKS.map(({ id, Icon, text, to, modal }, i) => {
            const inner = (
              <>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-steel-border text-steel-brass transition-colors duration-300 group-hover:border-steel-brass group-hover:bg-steel-brass/10">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm leading-snug text-steel-secondary transition-colors duration-300 group-hover:text-steel-text">
                  {text}
                </span>
                {(to || modal) && (
                  <ArrowUpRight className="ml-auto mt-1 h-3 w-3 shrink-0 text-steel-border transition-colors duration-300 group-hover:text-steel-brass" />
                )}
              </>
            );
            return (
              <motion.li
                key={id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.07, ease: EASE }}
              >
                {to ? (
                  <Link to={to} data-testid={`hero-perk-${id}`} className={itemClass}>
                    {inner}
                  </Link>
                ) : modal ? (
                  <button type="button" data-testid={`hero-perk-${id}`} onClick={() => setSupportOpen(true)} className={itemClass}>
                    {inner}
                  </button>
                ) : (
                  <div data-testid={`hero-perk-${id}`} className={itemClass}>
                    {inner}
                  </div>
                )}
              </motion.li>
            );
          })}
        </ul>

        <HoverCard openDelay={120} closeDelay={80}>
          <HoverCardTrigger asChild>
            <a
              href="https://www.irs.gov/payments"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="irs-pay-btn"
              className="group mt-5 flex items-center gap-3 border border-steel-brass/40 bg-steel-brass/5 px-4 py-3 transition-colors duration-300 hover:bg-steel-brass/15"
            >
              <Landmark className="h-4 w-4 shrink-0 text-steel-brass" />
              <span className="text-sm font-medium leading-snug text-steel-text">How to pay your taxes to the IRS</span>
              <ArrowUpRight className="ml-auto h-3.5 w-3.5 shrink-0 text-steel-brass transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </HoverCardTrigger>
          <HoverCardContent
            data-testid="irs-pay-info"
            className="w-80 border-steel-border bg-steel-elevated text-steel-text"
          >
            <p className="font-display text-sm font-medium text-steel-text">Paying the IRS directly</p>
            <p className="mt-2 text-xs leading-relaxed text-steel-secondary">
              Tax you owe is paid to the IRS — never to us. This opens IRS.gov, where you can pay by bank transfer
              (IRS Direct Pay, free), debit or credit card (card processor fees apply), or set up a payment plan if
              you can&rsquo;t pay it all at once.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-steel-muted">
              Our preparation fee is separate and is paid to us — by card, or out of your refund.
            </p>
          </HoverCardContent>
        </HoverCard>
      </div>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent
          data-testid="human-support-modal"
          className="border-steel-border bg-steel-surface text-steel-text sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-steel-text">
              <Mail className="h-5 w-5 text-steel-brass" /> Human Support Center
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed text-steel-secondary">
              Reach Jared directly at{" "}
              <a
                href="mailto:jared@steeleindustriesnh.com"
                data-testid="human-support-email-link"
                className="font-medium text-steel-brass underline"
              >
                jared@steeleindustriesnh.com
              </a>
              . Jared will contact you as soon as possible — between 10am and 7pm, you'll be
              contacted with extreme promptness.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HeroPerks;
